import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useRef, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { PromoProvider } from "@/context/PromoContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { OnboardingPopup } from "@/components/OnboardingPopup";
import { PromoBanner } from "@/components/PromoBanner";
import { PromoPopup } from "@/components/PromoPopup";
import Home from "@/pages/Home";

const Shop = lazy(() => import("@/pages/Shop"));
const Product = lazy(() => import("@/pages/Product"));
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const OrderConfirmed = lazy(() => import("@/pages/OrderConfirmed"));
const Admin = lazy(() => import("@/pages/Admin"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Policies = lazy(() => import("@/pages/Policies"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Journal = lazy(() => import("@/pages/Journal"));
const JournalArticle = lazy(() => import("@/pages/JournalArticle"));

const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#C5A059]/30 border-t-[#C5A059] rounded-full animate-spin" />
  </div>
);

const GA_MEASUREMENT_ID = "G-YJLW6PQP83";

// Standard GA4 install: gtag('config','G-YJLW6PQP83') in public/index.html sends the
// INITIAL page_view automatically. This only sends a page_view on subsequent client-side
// (SPA) route changes, with the real URL/title — so no duplicate initial hit.
const RouteAnalytics = () => {
  const { pathname, search } = useLocation();
  const isInitial = useRef(true);
  useEffect(() => {
    if (isInitial.current) {
      isInitial.current = false; // initial page_view already sent by gtag('config')
      return;
    }
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      page_path: pathname + (search || ""),
      send_to: GA_MEASUREMENT_ID,
    });
  }, [pathname, search]);
  return null;
};

const ScrollToHash = () => {
  const { pathname, hash, state } = useLocation();
  useEffect(() => {
    if (hash) {
      let tries = 0;
      const tryScroll = () => {
        const el = document.querySelector(hash);
        if (el) { el.scrollIntoView({ behavior: "smooth" }); }
        else if (tries++ < 20) { setTimeout(tryScroll, 100); }
      };
      tryScroll();
    } else if (!state?.restore) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [pathname, hash, state]);
  return null;
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <SettingsProvider>
          <CartProvider>
            <PromoProvider>
            <BrowserRouter>
              <ScrollToHash />
              <RouteAnalytics />
              <Header />
              <PromoBanner />
              <main>
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/fragrance/:id" element={<Product />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/order-confirmed" element={<OrderConfirmed />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/policies" element={<Policies />} />
                    <Route path="/journal" element={<Journal />} />
                    <Route path="/journal/:slug" element={<JournalArticle />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
              <OnboardingPopup />
              <PromoPopup />
              <Toaster position="bottom-right" theme="dark" />
            </BrowserRouter>
            </PromoProvider>
          </CartProvider>
        </SettingsProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
